import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { FiArrowRight, FiTrendingUp, FiShield, FiBarChart2, FiMapPin, FiUsers, FiDollarSign } from 'react-icons/fi';

const STATS = [
  { value: '500+', label: 'Active Drivers' },
  { value: '120+', label: 'Campaigns Run' },
  { value: '25', label: 'Districts Covered' },
  { value: '2M+', label: 'Impressions Delivered' },
];

export function LandingPage() {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 lg:pt-32 overflow-hidden">
        <div className="absolute inset-0 bg-slate-50 -z-10" />
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 w-[800px] h-[800px] bg-primary-50 rounded-full blur-3xl opacity-50 -z-10" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3 w-[600px] h-[600px] bg-accent-50 rounded-full blur-3xl opacity-50 -z-10" />

        <div className="container mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto space-y-8">
            <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-600 text-sm font-bold px-4 py-2 rounded-full border border-primary-100">
              <FiMapPin className="w-4 h-4" /> Now operating across all 25 Sri Lanka districts
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold text-slate-900 tracking-tight leading-[1.1]">
              The Modern{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-accent-500">
                Vehicle Advertising
              </span>{' '}
              Network
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Connect your brand with everyday drivers. Turn commutes into powerful, measurable ad campaigns across Sri Lanka.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Link to="/register">
                <Button size="lg" className="w-full sm:w-auto group">
                  Start Your Campaign
                  <FiArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/register?role=driver">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  Become a Driver
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {STATS.map((stat, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 text-center">
                <p className="text-3xl font-black text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500 font-medium mt-1">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-white" id="how-it-works">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-slate-600">Simple, transparent, and results-driven.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { step: '01', title: 'Create a Campaign', desc: 'Choose your vehicle type, panel, district, and duration. Get instant pricing.' },
              { step: '02', title: 'Drivers Apply', desc: 'Verified drivers in your target district apply to carry your brand.' },
              { step: '03', title: 'Track & Grow', desc: 'Monitor impressions, reach, and campaign performance in real time.' },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="relative p-8 rounded-2xl border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-lg transition-all duration-300">
                <span className="text-6xl font-black text-slate-100 absolute top-4 right-6">{item.step}</span>
                <h3 className="text-xl font-semibold text-slate-900 mb-3 relative z-10">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed relative z-10">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-slate-50" id="features">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Why choose Roaming Logos?</h2>
            <p className="text-slate-600">Built for scale, transparency, and results.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { icon: FiTrendingUp, title: 'Real-time Tracking', desc: 'Monitor your campaigns with GPS-verified impressions and route tracking.' },
              { icon: FiShield, title: 'Verified Drivers', desc: 'Every driver undergoes strict background checks and vehicle quality verification.' },
              { icon: FiBarChart2, title: 'Analytics Dashboard', desc: 'Comprehensive reporting on reach, frequency, and estimated ROI.' },
              { icon: FiMapPin, title: 'Island-wide Coverage', desc: 'All 25 Sri Lankan districts covered with a growing fleet of drivers.' },
              { icon: FiUsers, title: 'Flexible Formats', desc: 'TukTuks, Cars, and Vans — choose the vehicle that fits your brand.' },
              { icon: FiDollarSign, title: 'Transparent Pricing', desc: 'Fixed monthly pricing with no hidden fees. Know your total before you commit.' },
            ].map((feature, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="p-6 rounded-2xl border border-slate-100 bg-white hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-900">
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-2xl mx-auto">
            <h2 className="text-4xl font-black text-white mb-4">Ready to get your brand moving?</h2>
            <p className="text-slate-400 mb-8 text-lg">Join hundreds of Sri Lankan businesses already running campaigns on Roaming Logos.</p>
            <Link to="/register">
              <Button size="lg" className="bg-primary-500 hover:bg-primary-600 text-white shadow-lg shadow-primary-500/30">
                Start for Free <FiArrowRight className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}